import React, { useState } from "react";
import styled from "styled-components";
import { Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import * as Sentry from "@sentry/react";
import { request } from "hooks/useOkoApi/useOkoApi";
import { EmptyState } from "components/shared/EmptyState/EmptyState";

// Closes a running session. Shared by the history card's in-progress body and
// the live pill's panel, so the call and its error copy live in one place.
export const EndSessionButton = ({ sessionId, onSessionClosed, outline = false }) => {
  const [isClosingSession, setIsClosingSession] = useState(false);
  const [closeSessionError, setCloseSessionError] = useState(null);

  const handleCloseSession = async () => {
    setIsClosingSession(true);
    setCloseSessionError(null);
    try {
      const response = await request(`session/${sessionId}/close`, {
        method: 'POST',
      });
      if (response?.ended_at) {
        onSessionClosed(response.ended_at);
      }
    } catch (e) {
      console.error('Failed to close session:', e);
      Sentry.captureException(e);
      const errorMessage = e?.status === 403
        ? 'You are not authorized to close this session.'
        : e?.status === 409
        ? 'This session has already been closed.'
        : e?.detail || e?.message || 'Failed to close session. Please try again.';
      setCloseSessionError(errorMessage);
    } finally {
      setIsClosingSession(false);
    }
  };

  return (
    <>
      {closeSessionError && (
        <ErrorText>{closeSessionError}</ErrorText>
      )}
      <EndButton
        onClick={handleCloseSession}
        disabled={isClosingSession}
        $outline={outline}
      >
        {isClosingSession ? (
          <>
            <FontAwesomeIcon icon={faSpinner} spin />
            Ending Session...
          </>
        ) : (
          'End Session'
        )}
      </EndButton>
    </>
  );
};

export const OngoingSessionContent = ({ sessionId, onSessionClosed }) => (
  <Container>
    <EmptyState
      message="We are still supporting your students..."
      description={
        <>
          OKO is currently facilitating the group's collaboration. We'll start the deep analysis for your report the moment the session closes.
          <br /><br />
          <span style={{ fontWeight: 700, fontStyle: 'normal' }}>Is the session actually over?</span> Sometimes a student leaves a tab open, which keeps the session running. No problem—just click "End Session" below to wrap things up.
        </>
      }
    />
    <EndSessionButton sessionId={sessionId} onSessionClosed={onSessionClosed} />
  </Container>
);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const EndButton = styled(Button)`
  background-color: ${({ $outline }) => ($outline ? '#fff' : '#605DEC')};
  color: ${({ $outline }) => ($outline ? '#605DEC' : '#fff')};
  border: ${({ $outline }) => ($outline ? '1px solid #605DEC' : 'none')};
  font-weight: 600;
  font-size: 0.875rem;
  padding: 0.625rem 1.5rem;
  border-radius: 0.5rem;
  display: flex;
  gap: 0.5rem;
  align-items: center;

  &:hover {
    background-color: ${({ $outline }) => ($outline ? '#EEEEF9' : '#4B48D9')};
    color: ${({ $outline }) => ($outline ? '#605DEC' : '#fff')};
    border: ${({ $outline }) => ($outline ? '1px solid #605DEC' : 'none')};
  }

  &:disabled {
    background-color: ${({ $outline }) => ($outline ? '#fff' : '#605DEC')};
    color: ${({ $outline }) => ($outline ? '#605DEC' : '#fff')};
    opacity: 0.6;
    cursor: not-allowed;
  }

  /* Secondary (outline) style, used on the live pill's panel. */
  ${({ $outline }) => $outline && `
    height: 48px;
    padding: 0 20px;
    font-size: 18px;
    font-weight: 700;
    line-height: 26px;
    letter-spacing: 0.25px;
  `}
`;

const ErrorText = styled.p`
  color: #DC2626;
  font-size: 0.875rem;
  margin: 0 0 1rem 0;
  text-align: center;
`;
